import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "./useOrganization";

export interface PaymentMethod {
  id: string;
  organization_id: string;
  key: string;
  name: string;
  icon: string | null;
  enabled: boolean;
  is_system: boolean;
  is_hidden: boolean;
  sort_order: number;
  metadata: Record<string, unknown> | null;
}

export function usePaymentMethods() {
  const { organization } = useOrganization();
  const qc = useQueryClient();

  const { data: allMethods = [], isLoading } = useQuery({
    queryKey: ["payment-methods", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payment_methods" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PaymentMethod[];
    },
    enabled: !!organization?.id,
  });

  const visibleMethods = allMethods.filter((m) => !m.is_hidden);
  const hiddenMethods = allMethods.filter((m) => m.is_hidden);
  const activeMethods = allMethods.filter((m) => m.enabled && !m.is_hidden);

  // For dropdowns: { value: key, label: name } — only visible + enabled
  const paymentMethodOptions = activeMethods.map((m) => ({ value: m.key, label: m.name }));

  const getByKey = (key: string) => allMethods.find((m) => m.key === key);
  const getById = (id: string) => allMethods.find((m) => m.id === id);

  const updateMethod = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<PaymentMethod> }) => {
      const { error } = await supabase
        .from("payment_methods" as any)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payment-methods"] }),
  });

  const createMethod = useMutation({
    mutationFn: async (data: { key: string; name: string; icon?: string }) => {
      const { error } = await supabase.from("payment_methods" as any).insert({
        organization_id: organization!.id,
        key: data.key,
        name: data.name,
        icon: data.icon ?? "Tag",
        enabled: true,
        is_system: false,
        sort_order: 200,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payment-methods"] }),
  });

  const deleteMethod = useMutation({
    mutationFn: async (id: string) => {
      const method = getById(id);
      if (method?.is_system) throw new Error("Cannot delete system payment method");
      const { error } = await supabase.from("payment_methods" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payment-methods"] }),
  });

  return {
    allMethods,
    visibleMethods,
    hiddenMethods,
    activeMethods,
    paymentMethodOptions,
    isLoading,
    getByKey,
    getById,
    updateMethod,
    createMethod,
    deleteMethod,
  };
}
